import { useCallback, useEffect, useState } from "react";
import { useTicketScanMutation } from "@ticketingplatform/api/dist/ticket";
import {
  GET_EVENT_OCCURRENCES,
  GET_EVENT_OCCURRENCE,
} from "@ticketingplatform/api/dist/event";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { useEventOccurrenceContext } from "contexts/EventOccurrenceContext";
import { ApolloError } from "@apollo/client";
import { useIntl } from "react-intl";
import { useIsFocused } from "@react-navigation/native";
import { usePrevious } from "react-use";

import { UseQrScannerReturnType } from "./types";

export const useQrScanner = (scanDelay = 1000): UseQrScannerReturnType => {
  const isFocused = useIsFocused();
  const intl = useIntl();
  const { eventOccurrenceId } = useEventOccurrenceContext();
  const [$isValid, setIsValid] = useState<boolean>();
  const prevIsValid = usePrevious($isValid);
  const [error, setError] = useState<string>();
  const [, qrData, setQrData] = useDebouncedValue<string | undefined>(
    undefined,
    scanDelay
  );

  const onDecode = useCallback(
    (newQrData: string) => {
      if (newQrData === qrData) {
        setIsValid(prevIsValid);

        return;
      }

      setError(undefined);
      setQrData(newQrData);
    },
    [prevIsValid, qrData, setQrData]
  );

  const onError = useCallback(
    (newError: Error | ApolloError) => {
      if ("graphQLErrors" in newError) {
        setIsValid(false);
        setError(newError.message);
      } else {
        setError(
          intl.formatMessage({ defaultMessage: "Camera permission denied" })
        );
      }
    },
    [intl]
  );

  const [ticketScanMutation, { loading: $isLoading, data }] =
    useTicketScanMutation({
      evictQueries: [GET_EVENT_OCCURRENCES, GET_EVENT_OCCURRENCE],
      onError,
    });

  useEffect(() => {
    if ($isValid === undefined) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setIsValid(undefined);
    }, 2000);

    return () => {
      window.clearTimeout(timeout);
    };
  }, [$isValid]);

  useEffect(() => {
    if (!qrData) {
      return;
    }

    (async () => {
      const result = await ticketScanMutation({
        variables: {
          eventOccurrenceId,
          data: qrData,
          orderInclude: true,
        },
      });

      if (result.data?.ticketScan) {
        setIsValid(true);
      }
    })();
  }, [eventOccurrenceId, qrData, ticketScanMutation]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    setIsValid(undefined);
    setError(undefined);
    setQrData(undefined);
  }, [isFocused, setQrData]);

  return {
    scanDelay,
    $isValid,
    $isLoading,
    error,
    ticket: data?.ticketScan || undefined,
    onError,
    onDecode,
  };
};
